import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { eur, dateIt } from "@/lib/format";
import { Card, CardContent } from "@/components/ui/card";
import StatusBadge from "@/components/app/StatusBadge";
import PartnerCreditCard from "@/components/app/PartnerCreditCard";
import { Link } from "react-router-dom";
import { ShoppingCart, Users, GraduationCap, Wallet, ArrowRight, PiggyBank } from "lucide-react";

const KPI = ({ label, value, icon: Icon, tint, testid }) => (
  <div className="kpi-card" data-testid={testid}>
    <div className="flex items-center justify-between">
      <div className="text-xs uppercase tracking-widest font-bold text-amber-600">{label}</div>
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${tint}`}><Icon className="w-4 h-4" /></div>
    </div>
    <div className="mt-3 font-display text-3xl font-extrabold text-slate-900">{value}</div>
  </div>
);

export default function PartnerDashboard() {
  const [data, setData] = useState(null);
  const [credit, setCredit] = useState(null);
  const { user } = useAuth();
  useEffect(() => {
    api.get("/dashboard/partner").then(r => setData(r.data));
    api.get("/partners/me/credit").then(r => setCredit(r.data)).catch(() => {});
  }, []);
  if (!data) return <div className="text-slate-500">Caricamento…</div>;
  return (
    <div className="space-y-8" data-testid="partner-dashboard">
      <div>
        <h1 className="font-display text-3xl font-extrabold text-slate-900">Panoramica</h1>
        <p className="text-slate-500 mt-1">Riepilogo delle attività partner Centro Studi Athena.</p>
      </div>
      <PartnerCreditCard credit={credit || user?.partner} compact="minimal" showRecharge />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <KPI label="Ordini effettuati" value={data.orders_count} icon={ShoppingCart} tint="bg-blue-100 text-blue-700" testid="kpi-orders" />
        <KPI label="Iscrizioni" value={data.enrollments_count} icon={Users} tint="bg-emerald-100 text-emerald-700" testid="kpi-enrollments" />
        <KPI label="Corsi attivi" value={data.active_courses} icon={GraduationCap} tint="bg-amber-100 text-amber-700" testid="kpi-active" />
        <KPI label="Spesa totale" value={eur(data.total_spent)} icon={Wallet} tint="bg-indigo-100 text-indigo-700" testid="kpi-spent" />
        <KPI label="Risparmio totale" value={eur(data.total_saving || 0)} icon={PiggyBank} tint="bg-emerald-100 text-emerald-700" testid="kpi-saving" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 border-slate-200 athena-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-xl font-bold">Ultimi ordini</h2>
              <Link to="/partner/ordini" className="text-sm text-amber-600 font-semibold flex items-center gap-1" data-testid="link-all-orders">
                Vedi tutti <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            {data.recent_orders.length === 0 ? (
              <div className="text-slate-500 text-sm py-6">Nessun ordine ancora. Esplora il catalogo per iniziare.</div>
            ) : (
              <div className="divide-y divide-slate-100">
                {data.recent_orders.map((o) => (
                  <Link key={o.id} to={`/partner/ordini/${o.id}`} className="flex items-center py-3 hover:bg-slate-50 rounded-md px-2 -mx-2"
                    data-testid={`recent-order-${o.order_number}`}>
                    <div className="flex-1">
                      <div className="font-semibold text-slate-900">{o.order_number}</div>
                      <div className="text-xs text-slate-500">{dateIt(o.created_at)}</div>
                    </div>
                    <div className="text-right mr-4">
                      <div className="font-semibold text-slate-900">{eur(o.total)}</div>
                    </div>
                    <StatusBadge status={o.status} />
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        <Card className="border-slate-200 athena-shadow">
          <CardContent className="p-6">
            <h2 className="font-display text-xl font-bold mb-4">Attività recenti</h2>
            <ul className="space-y-3 text-sm">
              {data.recent_activity.length === 0 ? (
                <li className="text-slate-500">Nessuna attività recente.</li>
              ) : data.recent_activity.map((a) => (
                <li key={a.id} className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-amber-500 mt-1.5" />
                  <div>
                    <div className="text-slate-700"><span className="font-semibold capitalize">{a.action}</span> · {a.entity}</div>
                    <div className="text-xs text-slate-400">{dateIt(a.created_at)}</div>
                  </div>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
